import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { ToastMessages } from "../../../../../components/toasts/toastMessages";
import { showApiError } from "../../../../../utils/showApiError";

/**
 * Handles the refresh templates action for business office notes.
 *
 * Triggers an API call to refresh templates. Success/failure is signalled by
 * whether `runFetch` throws (HTTP-status contract); on failure, `showApiError`
 * surfaces `body.detail` for 4xx and `REFRESH_TEMPLATES_FAILURE` for 5xx /
 * non-HTTP errors. Optional `onSuccess` / `onFailure` callbacks let callers
 * run additional async work (e.g. refetching data, closing a modal) and are
 * awaited so callers can rely on completion ordering.
 *
 * @param runFetch  - The fetch function from useAbortableFetch hook that triggers the API call
 * @param loading   - Current loading state to prevent duplicate requests
 * @param onSuccess - Optional callback invoked after a successful refresh
 * @param onFailure - Optional callback invoked after a failed refresh (HTTP, network, or parse failure)
 * @returns Promise that resolves when the refresh operation (and any callback) completes
 *
 * @example
 * ```tsx
 * const { runFetch, loading } = useAbortableFetch(fetchFn);
 * await handleRefreshTemplates(runFetch, loading, async () => {
 *   await refetchAllData();
 * });
 * ```
 */
export const handleRefreshTemplates = async (
  runFetch: () => Promise<any>,
  loading: boolean,
  onSuccess?: () => void | Promise<void>,
  onFailure?: () => void | Promise<void>,
) => {
  if (loading) return;
  try {
    await runFetch();
    await onSuccess?.();
    toast.success(ToastMessages.REFRESH_TEMPLATES_SUCCESS);
  } catch (error) {
    await onFailure?.();
    showApiError(error, ToastMessages.REFRESH_TEMPLATES_FAILURE);
  }
};
